// §16 — the server HTTP contract, shared by the Fastify routes and the web client.
import { z } from 'zod';
import { SKIN_IDS } from './skin';

/** §16 — the uniform error code set and its HTTP status mapping. */
export const HTTP_ERROR_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  REV_CONFLICT: 409,
  /**
   * §26.8 F14 — an invite token that has expired or been used. **410, never 404**: the token *is*
   * the secret (§26.9), so there is nothing to protect by being vague, and "it existed and is
   * finished" is the difference between an offer to request a new link and a mistyped URL.
   */
  GONE: 410,
  PAYLOAD_TOO_LARGE: 413,
  /**
   * §30.4 — the shape was right and the value was refused.
   *
   * A daily score whose `day` is not the server's current UTC date is not a malformed request: the
   * client sent a well-formed row for a board it may not write to. 422 lets it say so — a clock a
   * day out is worth an honest message rather than the same 400 a typo gets.
   */
  UNPROCESSABLE: 422,
  RATE_LIMITED: 429,
  INTERNAL: 500,
  /**
   * §34.8 — the forecast provider could not be reached **and** there was nothing cached to fall back
   * to (D-569).
   *
   * Deliberately not `INTERNAL`: nothing here failed. The request was well-formed, the server is
   * healthy, and somebody else's server is not answering — which is a different thing to tell a client,
   * because the honest response to it is "try later", not "something is broken". It is also the only
   * §16 code whose cause is outside this fleet, which is why it did not exist until the first egress
   * route did.
   */
  SERVICE_UNAVAILABLE: 503,
  QUOTA_EXCEEDED: 507,

  // ── §34.13 (M66) — the two refusals a file offer can answer with. Both are **409**, which §34.13
  //    states outright, and neither of the 409s already here fits: `REV_CONFLICT` means "the file
  //    changed since you last read it" and would send a client into D-045's retry, and
  //    `QUOTA_EXCEEDED` is 507 — the code an *upload* over the limit gets, which is a different
  //    conversation from "somebody offered you a file you have no room for".
  //
  //    Two codes rather than one because each maps to exactly one binding literal in PLAN §7.5, and
  //    a client that had to infer which sentence to show from which route it called is a client
  //    that shows the wrong one the first time the routes move.
  /** §34.13 — the sender's file has no synced blob, so there is nothing to offer. */
  OFFER_DEVICE_LOCAL: 409,
  /** §34.13 — accepting would put the *recipient* over their quota. The offer stays pending. */
  OFFER_NO_SPACE: 409,
} as const satisfies Record<string, number>;

export type HttpErrorCode = keyof typeof HTTP_ERROR_STATUS;

export const errorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

export type ErrorBody = z.infer<typeof errorBodySchema>;

/** §16 `GET /api/config` — the one public, unauthenticated payload. */
export const publicConfigSchema = z.object({
  anonymousAccess: z.boolean(),
  version: z.string(),
  skins: z.array(z.enum(SKIN_IDS)),
});

export type PublicConfig = z.infer<typeof publicConfigSchema>;

/**
 * §16 `GET /api/me` — `{user, isAdmin, quota}`.
 *
 * `email` is nullable rather than optional because §17 lets it be null and the client renders a
 * different thing for "no email" than for "field absent" — one is a fact about the account, the
 * other would be a fact about the response.
 */
export const meResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
  }),
  isAdmin: z.boolean(),
  /**
   * §26.1 (D-220) — this server's `HANGAR_INSTANCE`, so the client never guesses the other half of
   * its own ref.
   *
   * **Optional on the wire**, required in spirit. A ref is `<id>@<instance>` and the instance is
   * the server's configuration; a client that fell back to `location.hostname` would namespace
   * under a name nothing else uses and every social query would quietly answer nothing. Optional
   * because an older server has none and a version skew must degrade to "no social layer" rather
   * than to a failed parse of the whole identity response.
   */
  instance: z.string().optional(),
  quota: z.object({
    /** Bytes of live (non-trashed) file content this user is holding on the server. */
    usedBytes: z.number().int().nonnegative(),
    limitBytes: z.number().int().positive(),
  }),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

/**
 * §7.4 / §16 `GET /api/sync/changes?since=` — `{cursor, changes: [{path, rev, sha, size, op}]}`.
 *
 * One entry per **path**, carrying that path's latest state within the range — not a replay of
 * every revision it passed through. A cursor's job is to get a client to the current state, and
 * shipping the intermediate revisions of a file somebody saved forty times would have it download
 * thirty-nine versions it is about to overwrite.
 */
export const syncChangeSchema = z.object({
  path: z.string(),
  rev: z.number().int().nonnegative(),
  /** Absent for a `delete`, and for a directory. */
  sha: z.string().optional(),
  size: z.number().int().nonnegative(),
  op: z.enum(['put', 'delete', 'move']),
  isDir: z.boolean(),
});

export type SyncChange = z.infer<typeof syncChangeSchema>;

export const syncChangesResponseSchema = z.object({
  /** Feed this back as `since` next time. Monotonic; never goes backwards. */
  cursor: z.number().int().nonnegative(),
  changes: z.array(syncChangeSchema),
  /** True when the cursor stopped at a page boundary and another call would return more. */
  more: z.boolean(),
});

export type SyncChangesResponse = z.infer<typeof syncChangesResponseSchema>;

/** §16 — how many changes one `GET /api/sync/changes` will return. */
export const SYNC_PAGE_SIZE = 500;

/** §16 `GET /healthz` — public, and its whole job is the database probe behind it. */
export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  version: z.string(),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;

/** §16 limits. */
/**
 * §19.1 — the shell origin's Content-Security-Policy.
 *
 * One constant, in the one package the server and the build config both read, because a policy
 * that differs between where it is served and where it is tested is not a policy.
 *
 * **It is §19.1's string with one directive added: `connect-src 'self' https:` (D-178.)** §19.1
 * as written omits `connect-src`, which therefore falls back to `default-src 'self'` — and that
 * blocks the two cross-origin requests the app model is built on: §9.2's
 * `GET https://<origin>/.well-known/hangar-app.json`, without which **no app can ever be
 * installed**, and §9.6's `HEAD` health probe, without which every icon dims. Measured, not
 * inferred: with the policy exactly as §19.1 writes it, Chromium answers
 * *"Refused to connect … 'connect-src' was not explicitly set, so 'default-src' is used"* and the
 * Store fails on every card. `https:` mirrors `frame-src https:` — the same statement that an
 * installed app is an https origin — and loosens nothing else.
 *
 * **§34.6 (M60) adds `media-src 'self' blob:` — D-527.** Without it `<audio>`/`<video>` fall back to
 * `default-src 'self'`, and a **blob URL is not `'self'`**: Chromium refuses every one of them with
 * *"Loading media from 'blob:…' violates … 'media-src' was not explicitly set, so 'default-src' is
 * used as a fallback"*, and the element reports `MEDIA_ERR_SRC_NOT_SUPPORTED` — indistinguishable,
 * from inside the app, from a codec this browser cannot play. So Media rendered §34.6's codec
 * refusal for a perfectly good WAV. **Lane C found the identical directive missing independently**
 * (D-562, M62.2): Chromium answered *"Media load rejected by URL safety check"* on a voice memo it
 * had just finished encoding — 34 767 bytes of `audio/webm; codecs=opus`, with `canPlayType`
 * answering `"probably"` at the same moment. Two apps, two codecs, two error messages, one cause;
 * the agreement is why this is recorded as a policy gap rather than as either app's bug.
 * `img-src` already carries `blob:` for exactly this reason
 * (that is how Peek shows a picture); media had no directive of its own to carry it. Same-origin
 * plus the blob scheme, mirroring `img-src` — no third-party origin, so §34.15's "exactly two
 * third-party origins" still holds and its arch assertion is unaffected.
 *
 * **§34.15 (M61) names the two third-party origins**: `frame-src` gains
 * `https://www.youtube-nocookie.com` (the embed) and `img-src` gains `https://i.ytimg.com` (the
 * thumbnails). Both directives already carry a bare `https:`, so — CSP source lists being unions —
 * neither addition *permits* anything that was not already permitted. They are **declarative**: they
 * put the only two third-party hosts Hangar talks to in the policy, where `no-proxy.test.ts` can
 * assert that the set is exactly those two and grep for a third. That assertion is the deliverable;
 * the string change is how it becomes possible (D-529).
 *
 * Do not read the origins as making `https:` removable. `frame-src https:` is §9.2's statement that
 * an installed app is an https origin, and `connect-src https:` is what lets one be installed at
 * all — dropping either would break every external app (D-178).
 */
export const CONTENT_SECURITY_POLICY =
  "default-src 'self'; connect-src 'self' https:; " +
  'frame-src https: https://www.youtube-nocookie.com; ' +
  "img-src 'self' data: blob: https: https://i.ytimg.com; media-src 'self' blob:; " +
  "script-src 'self'; style-src 'self' 'unsafe-inline'; " +
  "frame-ancestors 'none'";

/**
 * The rest of the security header pass (D-175).
 *
 * §19.1 names the CSP and nothing else, so these are the smallest set that costs nothing and
 * closes a known hole each: `nosniff` stops a text file being executed as script,
 * `X-Frame-Options` is `frame-ancestors`' older twin for anything that predates CSP 2, and the
 * referrer policy keeps a Hangar path out of a third-party's logs when a user follows a link out.
 */
export const SECURITY_HEADERS: Readonly<Record<string, string>> = {
  'content-security-policy': CONTENT_SECURITY_POLICY,
  'x-content-type-options': 'nosniff',
  'x-frame-options': 'DENY',
  'referrer-policy': 'strict-origin-when-cross-origin',
};

export const LIMITS = {
  jsonBodyBytes: 1_000_000,
  blobUploadBytes: 100_000_000,
  writesPerMinute: 60,
  readsPerMinute: 600,
  /**
   * §16 / §26.5 — "Chat posts are metered at **20/min/user**."
   *
   * A third bucket rather than a tighter general write limit, because the two protect different
   * things: 60 writes/min is what keeps a runaway sync client off the server, and 20 posts/min is
   * about a person. They also must not share a budget — a chat that spent the write allowance
   * would 429 the file saves, which is exactly the cross-feature failure D-193 argues against.
   */
  chatPostsPerMinute: 20,
  /**
   * §16 / §26.7 (D-350) — every `/api/play/*` request, read or write, in **one** bucket.
   *
   * §16's 60 writes/min was written for a filesystem, and a co-op game is not one: two players
   * firing produces several moves a second, each of which rings a doorbell the peer answers with a
   * `GET`. Metered against the general budget, a minute of Intercept would 429 the file saves — the
   * cross-feature failure D-193 argues against, arriving as "sync stopped working".
   *
   * One bucket for both directions because they are the same activity. **1 500** since M41
   * (D-377): D-350 sized this for games that generate moves *sparsely*, and Sortie's co-op does
   * not — continuous eight-way movement produces an input every tick, which even batched at
   * §30.3's 10 Hz cadence is ~600 posts plus ~600 doorbell `GET`s a minute, exactly the bucket. So
   * the number moves rather than the design pretending the arithmetic works. D-350's *shape*
   * survives untouched — one bucket, both directions, per user — and 1 500 is still an order of
   * magnitude below anything that would trouble SQLite. A session that hits it is a client in a
   * loop, which is what a limit is for.
   */
  playPerMinute: 1500,
} as const;
